import { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Typography,
  Divider,
  Alert,
} from '@mui/material'
import { useAdventureEditorStore } from '../../../stores/adventureEditorStore'
import {
  VotingVisibility,
  VoteChangePolicy,
  VotingCompletionMode,
  VotingTieResolution,
  OfflineMemberAction,
  VOTING_VISIBILITY_LABELS,
  VOTE_CHANGE_POLICY_LABELS,
  VOTING_COMPLETION_MODE_LABELS,
  VOTING_TIE_RESOLUTION_LABELS,
  OFFLINE_MEMBER_ACTION_LABELS,
  type GroupParticipationSettingsDto,
} from '../../../types/adventure-editor.types'
import { useCollaborativeAdventureEditor } from '@hooks/useCollaborativeAdventureEditor'
import { usePublishedPermissions } from '../../../hooks/usePublishedPermissions'
import { settingsApi } from '@api/endpoints/settings'
import { MeasurementUnit } from '@/types/settings'

// Conversion helpers
const feetToMeters = (feet: number): number => Math.round(feet * 0.3048)
const metersToFeet = (meters: number): number => Math.round(meters / 0.3048)

// Default settings for new group participation
const DEFAULT_GROUP_SETTINGS: GroupParticipationSettingsDto = {
  votingVisibility: VotingVisibility.GroupDefined,
  voteChangePolicy: VoteChangePolicy.GroupDefined,
  voteChangeWindowSeconds: undefined,
  votingCompletionMode: VotingCompletionMode.GroupDefined,
  votingCompletionPercentage: undefined,
  votingCompletionTimerSeconds: undefined,
  votingTieResolution: VotingTieResolution.GroupDefined,
  votingTieTimeoutSeconds: undefined,
  offlineMemberAction: OfflineMemberAction.GroupDefined,
  maxParticipantDistanceFeet: undefined,
  participantProximityPercentage: undefined,
}

export function GroupsPanel() {
  const generalInfo = useAdventureEditorStore((state) => state.generalInfo)
  const { updateGeneralInfoField } = useCollaborativeAdventureEditor()
  const { isPublished } = usePublishedPermissions()

  // Measurement unit preference (default to Imperial)
  const [measurementUnit, setMeasurementUnit] = useState<MeasurementUnit>(MeasurementUnit.Imperial)

  // Fetch user preferences for measurement unit
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const prefs = await settingsApi.getUserPreferences()
        setMeasurementUnit(prefs.measurementUnit ?? MeasurementUnit.Imperial)
      } catch {
        // Default to Imperial if fetch fails
      }
    }
    loadPreferences()
  }, [])

  const isMetric = measurementUnit === MeasurementUnit.Metric

  // Get current settings or defaults
  const settings: GroupParticipationSettingsDto = generalInfo.groupParticipationSettings || DEFAULT_GROUP_SETTINGS

  // Helper to update a specific setting
  const updateSetting = <K extends keyof GroupParticipationSettingsDto>(
    key: K,
    value: GroupParticipationSettingsDto[K]
  ) => {
    const newSettings = {
      ...settings,
      [key]: value,
    }
    updateGeneralInfoField('groupParticipationSettings', newSettings)
  }

  // Convert seconds to display format (minutes:seconds)
  const formatSeconds = (seconds: number | undefined): string => {
    if (seconds === undefined) return ''
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
  }

  // Convert feet to display format based on measurement unit
  const formatDistance = (feet: number | undefined): string => {
    if (feet === undefined || feet === 0) return 'Unlimited'
    if (isMetric) {
      const meters = feetToMeters(feet)
      return `${meters} m`
    } else {
      return `${feet} ft`
    }
  }

  // Get display value for slider based on measurement unit
  const getDisplayDistanceValue = (feet: number): number => {
    return isMetric ? feetToMeters(feet) : feet
  }

  // Convert display value back to feet for storage
  const getStoredDistanceValue = (displayValue: number): number => {
    return isMetric ? metersToFeet(displayValue) : displayValue
  }

  // Slider configuration based on measurement unit
  // Imperial: 25-1000 feet
  // Metric: ~8-305 meters (converted from feet range)
  const minFeet = 25
  const maxFeet = 1000
  const minMeters = feetToMeters(minFeet) // ~8m
  const maxMeters = feetToMeters(maxFeet) // ~305m

  const distanceSliderConfig = isMetric
    ? {
        min: 0,
        max: maxMeters,
        step: 5,
        marks: [
          { value: 0, label: 'None' },
          { value: minMeters, label: `${minMeters}m` },
          { value: 50, label: '50m' },
          { value: 100, label: '100m' },
          { value: 200, label: '200m' },
          { value: maxMeters, label: `${maxMeters}m` },
        ],
      }
    : {
        min: 0,
        max: maxFeet,
        step: 25,
        marks: [
          { value: 0, label: 'None' },
          { value: minFeet, label: `${minFeet}ft` },
          { value: 100, label: '100ft' },
          { value: 250, label: '250ft' },
          { value: 500, label: '500ft' },
          { value: maxFeet, label: `${maxFeet}ft` },
        ],
      }

  const disabled = isPublished

  // Current display value for the slider
  const currentDistanceDisplay = settings.maxParticipantDistanceFeet
    ? getDisplayDistanceValue(settings.maxParticipantDistanceFeet)
    : 0

  return (
    <Card>
      <CardHeader
        title="Group Participation Settings"
        subheader="Configure how groups participate in this adventure"
      />
      <CardContent>
        {!generalInfo.allowGroups && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Enable "Allow Groups" in the Settings panel to configure group participation.
          </Alert>
        )}

        {/* Voting Visibility */}
        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          Voting Visibility
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Controls how votes are displayed to group members during voting.
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={disabled}>
              <InputLabel>Voting Visibility</InputLabel>
              <Select
                value={settings.votingVisibility}
                label="Voting Visibility"
                onChange={(e) => updateSetting('votingVisibility', e.target.value as VotingVisibility)}
              >
                {Object.entries(VOTING_VISIBILITY_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={Number(value)}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {settings.votingVisibility === VotingVisibility.GroupDefined
                  ? 'The group decides how votes are displayed'
                  : settings.votingVisibility === VotingVisibility.HiddenUntilCompleted
                  ? 'Votes are hidden until all members have voted'
                  : 'Votes are shown in real-time as they are cast'}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Vote Change Policy */}
        <Typography variant="h6" gutterBottom>
          Vote Change Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Determines if and when group members can change their votes.
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={disabled}>
              <InputLabel>Vote Change Policy</InputLabel>
              <Select
                value={settings.voteChangePolicy}
                label="Vote Change Policy"
                onChange={(e) => updateSetting('voteChangePolicy', e.target.value as VoteChangePolicy)}
              >
                {Object.entries(VOTE_CHANGE_POLICY_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={Number(value)}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {settings.voteChangePolicy === VoteChangePolicy.GroupDefined
                  ? 'The group decides the vote change rules'
                  : settings.voteChangePolicy === VoteChangePolicy.LockedAfterSubmission
                  ? 'Votes cannot be changed after submission'
                  : settings.voteChangePolicy === VoteChangePolicy.LimitedWindow
                  ? 'Votes can be changed within a time window'
                  : 'Votes can be changed until voting closes'}
              </FormHelperText>
            </FormControl>
          </Grid>
          {settings.voteChangePolicy === VoteChangePolicy.LimitedWindow && (
            <Grid item xs={12} md={6}>
              <Box sx={{ px: 2 }}>
                <Typography gutterBottom>
                  Change Window: {formatSeconds(settings.voteChangeWindowSeconds) || '60s'}
                </Typography>
                <Slider
                  value={settings.voteChangeWindowSeconds || 60}
                  onChange={(_, value) => updateSetting('voteChangeWindowSeconds', value as number)}
                  min={25}
                  max={900}
                  step={5}
                  marks={[
                    { value: 25, label: '25s' },
                    { value: 300, label: '5m' },
                    { value: 600, label: '10m' },
                    { value: 900, label: '15m' },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={formatSeconds}
                  disabled={disabled}
                />
              </Box>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Voting Completion Settings */}
        <Typography variant="h6" gutterBottom>
          Voting Completion
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Defines when voting is considered complete.
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={disabled}>
              <InputLabel>Completion Mode</InputLabel>
              <Select
                value={settings.votingCompletionMode}
                label="Completion Mode"
                onChange={(e) => updateSetting('votingCompletionMode', e.target.value as VotingCompletionMode)}
              >
                {Object.entries(VOTING_COMPLETION_MODE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={Number(value)}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {settings.votingCompletionMode === VotingCompletionMode.GroupDefined
                  ? 'The group decides when voting is complete'
                  : settings.votingCompletionMode === VotingCompletionMode.VotingPercentage
                  ? 'Complete when a percentage of members have voted'
                  : 'Complete after a set time period'}
              </FormHelperText>
            </FormControl>
          </Grid>
          {settings.votingCompletionMode === VotingCompletionMode.VotingPercentage && (
            <Grid item xs={12} md={6}>
              <Box sx={{ px: 2 }}>
                <Typography gutterBottom>
                  Required Percentage: {settings.votingCompletionPercentage || 75}%
                </Typography>
                <Slider
                  value={settings.votingCompletionPercentage || 75}
                  onChange={(_, value) => updateSetting('votingCompletionPercentage', value as number)}
                  min={25}
                  max={100}
                  step={5}
                  marks={[
                    { value: 25, label: '25%' },
                    { value: 50, label: '50%' },
                    { value: 75, label: '75%' },
                    { value: 100, label: '100%' },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) => `${v}%`}
                  disabled={disabled}
                />
              </Box>
            </Grid>
          )}
          {settings.votingCompletionMode === VotingCompletionMode.TimerBased && (
            <Grid item xs={12} md={6}>
              <Box sx={{ px: 2 }}>
                <Typography gutterBottom>
                  Voting Timer: {formatSeconds(settings.votingCompletionTimerSeconds) || '5m'}
                </Typography>
                <Slider
                  value={settings.votingCompletionTimerSeconds || 300}
                  onChange={(_, value) => updateSetting('votingCompletionTimerSeconds', value as number)}
                  min={25}
                  max={900}
                  step={5}
                  marks={[
                    { value: 25, label: '25s' },
                    { value: 300, label: '5m' },
                    { value: 600, label: '10m' },
                    { value: 900, label: '15m' },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={formatSeconds}
                  disabled={disabled}
                />
              </Box>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Voting Tie Resolution */}
        <Typography variant="h6" gutterBottom>
          Tie Resolution
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          How to resolve when votes are tied.
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={disabled}>
              <InputLabel>Tie Resolution</InputLabel>
              <Select
                value={settings.votingTieResolution}
                label="Tie Resolution"
                onChange={(e) => updateSetting('votingTieResolution', e.target.value as VotingTieResolution)}
              >
                {Object.entries(VOTING_TIE_RESOLUTION_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={Number(value)}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {settings.votingTieResolution === VotingTieResolution.GroupDefined
                  ? 'The group decides how to handle ties'
                  : settings.votingTieResolution === VotingTieResolution.TimeoutRandomSelection
                  ? 'After timeout, randomly select from tied options'
                  : 'Allow members to revote on only the tied options'}
              </FormHelperText>
            </FormControl>
          </Grid>
          {settings.votingTieResolution === VotingTieResolution.TimeoutRandomSelection && (
            <Grid item xs={12} md={6}>
              <Box sx={{ px: 2 }}>
                <Typography gutterBottom>
                  Tie Timeout: {formatSeconds(settings.votingTieTimeoutSeconds) || '60s'}
                </Typography>
                <Slider
                  value={settings.votingTieTimeoutSeconds || 60}
                  onChange={(_, value) => updateSetting('votingTieTimeoutSeconds', value as number)}
                  min={25}
                  max={900}
                  step={5}
                  marks={[
                    { value: 25, label: '25s' },
                    { value: 300, label: '5m' },
                    { value: 600, label: '10m' },
                    { value: 900, label: '15m' },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={formatSeconds}
                  disabled={disabled}
                />
              </Box>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Offline Member Handling */}
        <Typography variant="h6" gutterBottom>
          Offline Member Handling
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          What happens when group members go offline during the adventure.
        </Typography>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={disabled}>
              <InputLabel>Offline Action</InputLabel>
              <Select
                value={settings.offlineMemberAction}
                label="Offline Action"
                onChange={(e) => updateSetting('offlineMemberAction', e.target.value as OfflineMemberAction)}
              >
                {Object.entries(OFFLINE_MEMBER_ACTION_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={Number(value)}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {settings.offlineMemberAction === OfflineMemberAction.GroupDefined
                  ? 'The group decides how to handle offline members'
                  : settings.offlineMemberAction === OfflineMemberAction.Pause
                  ? 'Pause the adventure until all members are online'
                  : settings.offlineMemberAction === OfflineMemberAction.Continue
                  ? 'Continue without waiting for offline members'
                  : settings.offlineMemberAction === OfflineMemberAction.Abandon
                  ? 'End the adventure if members go offline'
                  : 'Let the online members vote on what to do'}
              </FormHelperText>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Group Participation Boundaries */}
        <Typography variant="h6" gutterBottom>
          Proximity Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Control how close group members need to be to each other.
        </Typography>
        <Box sx={{ px: 2 }}>
          <Typography gutterBottom>
            Max Distance Between Participants: {formatDistance(settings.maxParticipantDistanceFeet)}
          </Typography>
          <Slider
            value={currentDistanceDisplay}
            onChange={(_, value) => {
              const storedValue = getStoredDistanceValue(value as number)
              updateSetting('maxParticipantDistanceFeet', storedValue || undefined)
            }}
            min={distanceSliderConfig.min}
            max={distanceSliderConfig.max}
            step={distanceSliderConfig.step}
            marks={distanceSliderConfig.marks}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => {
              if (v === 0) return 'Unlimited'
              return isMetric ? `${v} m` : `${v} ft`
            }}
            disabled={disabled}
          />
          <FormHelperText sx={{ mb: 3 }}>
            Maximum allowed distance between any two participants (0 = unlimited)
          </FormHelperText>
        </Box>
        <Box sx={{ px: 2 }}>
          <Typography gutterBottom>
            Proximity Percentage: {settings.participantProximityPercentage ?? 100}%
          </Typography>
          <Slider
            value={settings.participantProximityPercentage ?? 100}
            onChange={(_, value) => updateSetting('participantProximityPercentage', value as number)}
            min={0}
            max={100}
            step={5}
            marks={[
              { value: 0, label: '0%' },
              { value: 50, label: '50%' },
              { value: 100, label: '100%' },
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
            disabled={disabled}
          />
          <FormHelperText>
            Percentage of participants that must be within the max distance
          </FormHelperText>
        </Box>
      </CardContent>
    </Card>
  )
}
